import { Fragment } from 'react'

import { ButtonTypes, LinkTypes } from '@cox/core-ui8/dist/Button'
import OffersLoaded from '@cox/core-ui8/dist/OffersLoaded'
import StoreButton from '@cox/core-ui8/dist/StoreButton'
import { useFlexOffers } from '@cox/core-ui8/dist/useFlexOffers'

import ReactStandardTeaserSkeleton from './ReactStandardTeaserSkeleton'
import { useInversionFlags } from '../../../hooks/useInversionFlags'
import { DynamicMediaComponents, getDynamicMediaFromSrc } from '../../../utils/dynamic-media-util'
import { getImageSrc } from '../../../utils/local-util'
import { getAttributeValue, getConditionResult, hasCFConditionals } from '../../../utils/rule-builder-util'
import { getAnchorInfo } from '../../../utils/teaser-util'
import { RuleType } from '../../../utils/types'
import ReactBadge from '../../ReactBadge'
import ReactButton from '../../ReactButton'
import ReactImage from '../../ReactImage'
import ReactPrice, { PriceVariant } from '../../ReactPrice'
import ReactText from '../../ReactText'
import {
  BackgroundVariation,
  ContentFragment,
  Elements,
  ImageAlignment,
  ReactStandardTeaserProps,
  TeaserType,
  variantClass,
  Variation
} from '../types'

import './ReactStandardTeaser.scss'

const ReactStandardTeaser = (props: ReactStandardTeaserProps): JSX.Element => {
  const {
    cardType,
    condition,
    contentFragments,
    ruleExpression = '',
    ruleType,
    variation,
    tokenProperties,
    priceTagIcon,
    backgroundVariation,
    contentType,
    imageAlignment = ImageAlignment.LEFT
  } = props

  const flexOffers = useFlexOffers(ruleExpression, contentType, contentFragments)
  const { inversionProperties, isLocal } = useInversionFlags()

  // **************** Determines the card color / background variation ********************** //
  const backgroundVariant =
    backgroundVariation && backgroundVariation !== BackgroundVariation.SELECT
      ? `${backgroundVariation}-background-variation`
      : ''

  const renderCard = (elements: Elements, id: string) => {
    const {
      contentFragmentId = { value: '' },
      image = { value: '' },
      imageParams = { value: '' },
      altImage = { value: '' },
      contentBadgeColor = { value: '' },
      contentBadgeText = { value: '' },
      eyebrow = { value: '' },
      heading = { value: '' },
      description = { value: '' },
      price = { value: '' },
      scratchPrice = { value: '' },
      term = { value: '' },
      discount = { value: '' },
      /* PRIMARY BUTTON */
      buttonTextPrimary = { value: '' },
      buttonLinkTypePrimary = { value: '' },
      buttonLinkNewTabPrimary = { value: '' },
      buttonLinkPrimary = { value: '' },
      linkTypeButtonPrimary = { value: '' },
      linkIdButtonPrimary = { value: '' },
      /* SECONDARY BUTTON */
      buttonTextSecondary = { value: '' },
      buttonLinkTypeSecondary = { value: '' },
      buttonLinkNewTabSecondary = { value: '' },
      buttonLinkSecondary = { value: '' },
      linkTypeButtonSecondary = { value: '' },
      linkIdButtonSecondary = { value: '' },
      /* TERTIARY BUTTON */
      buttonTextTertiary = { value: '' },
      buttonLinkTypeTertiary = { value: '' },
      buttonLinkNewTabTertiary = { value: '' },
      buttonLinkTertiary = { value: '' },
      linkTypeButtonTertiary = { value: '' },
      linkIdButtonTertiary = { value: '' },
      appStoreImage = { value: '' },
      appStoreLink = { value: '' },
      appStoreAltText = { value: '' },
      playStoreImage = { value: '' },
      playStoreLink = { value: '' },
      playStoreAltText = { value: '' }
    } = elements
    const {
      hasAnchorLink: hasAnchorLinkPrimary,
      anchorUrl: anchorUrlPrimary,
      anchorId: anchorIdPrimary
    } = getAnchorInfo(buttonLinkPrimary?.value)
    const {
      hasAnchorLink: hasAnchorLinkSecondary,
      anchorUrl: anchorUrlSecondary,
      anchorId: anchorIdSecondary
    } = getAnchorInfo(buttonLinkSecondary?.value)
    const {
      hasAnchorLink: hasAnchorLinkTertiary,
      anchorUrl: anchorUrlTertiary,
      anchorId: anchorIdTertiary
    } = getAnchorInfo(buttonLinkTertiary?.value)

    const getValue = (attribute: any, propertyName?: string) => {
      if (hasCFConditionals(attribute)) {
        const jsonAttribute = (elements as any)?.[`${propertyName}_json`]
        return getAttributeValue(
          jsonAttribute,
          flexOffers,
          0,
          tokenProperties,
          propertyName,
          false,
          'displaytext',
          isLocal,
          inversionProperties
        )
      }
      return getAttributeValue(
        attribute,
        flexOffers,
        0,
        tokenProperties,
        propertyName,
        false,
        'displaytext',
        isLocal,
        inversionProperties
      )
    }
    const contentFragmentIdValue = getValue(contentFragmentId, 'contentFragmentId')
    const contentBadgeTextValue = getValue(contentBadgeText, 'contentBadgeText')
    const contentBadgeColorValue = getValue(contentBadgeColor, 'contentBadgeColor')
    const altImageValue = getValue(altImage, 'altImage')
    const imageParamsValue = getValue(imageParams, 'imageParams')
    const imageValue = getDynamicMediaFromSrc(
      getValue(image) as string,
      (imageParamsValue as DynamicMediaComponents) || getDynamicMediaComponentName()
    )
    const eyebrowValue = getValue(eyebrow, 'eyebrow')
    const headingValue = getValue(heading, 'heading')
    const descriptionValue = getValue(description, 'description')
    const priceValue = getValue(price, 'price')
    const scratchPriceValue = getValue(scratchPrice, 'scratchPrice')
    const termValue = getValue(term, 'term')
    const discountValue = getValue(discount, 'discount')

    /* PRIMARY BUTTON */
    const getButtonTypeForPrimary = () => {
      if (buttonLinkTypePrimary?.value) {
        return buttonLinkTypePrimary?.value as ButtonTypes
      }
      return cardType === TeaserType.standard || cardType === TeaserType.commerce
        ? ButtonTypes.PRIMARYALT
        : ButtonTypes.PRIMARY
    }
    const buttonTextPrimaryValue = getValue(buttonTextPrimary, 'buttonTextPrimary')
    const buttonLinkPrimaryValue = getValue(buttonLinkPrimary, 'buttonLinkPrimary')
    const buttonLinkNewTabPrimaryValue = getValue(buttonLinkNewTabPrimary, 'buttonLinkNewTabPrimary')
    const linkIdButtonPrimaryValue = getValue(linkIdButtonPrimary, 'linkIdButtonPrimary')
    const linkTypeButtonPrimaryValue = linkTypeButtonPrimary?.value
    const primaryButtonLink = hasAnchorLinkPrimary ? anchorUrlPrimary : buttonLinkPrimaryValue

    /* SECONDARY BUTTON */ const buttonLinkTypeSecondaryValue = buttonLinkTypeSecondary?.value || ButtonTypes.SECONDARY
    const buttonTextSecondaryValue = getValue(buttonTextSecondary, 'buttonTextSecondary')
    const buttonLinkSecondaryValue = getValue(buttonLinkSecondary, 'buttonLinkSecondary')
    const buttonLinkNewTabSecondaryValue = getValue(buttonLinkNewTabSecondary, 'buttonLinkNewTabSecondary')
    const linkIdButtonSecondaryValue = getValue(linkIdButtonSecondary, 'linkIdButtonSecondary')
    const linkTypeButtonSecondaryValue = linkTypeButtonSecondary?.value
    const secondaryButtonLink = hasAnchorLinkSecondary ? anchorUrlSecondary : buttonLinkSecondaryValue

    /* TERTIARY BUTTON */
    const buttonLinkTypeTertiaryValue = buttonLinkTypeTertiary?.value || ButtonTypes.TERTIARY
    const buttonTextTertiaryValue = getValue(buttonTextTertiary, 'buttonTextTertiary')
    const buttonLinkTertiaryValue = getValue(buttonLinkTertiary, 'buttonLinkTertiary')
    const buttonLinkNewTabTertiaryValue = getValue(buttonLinkNewTabTertiary, 'buttonLinkNewTabTertiary')
    const linkIdButtonTertiaryValue = getValue(linkIdButtonTertiary, 'linkIdButtonTertiary')
    const linkTypeButtonTertiaryValue = linkTypeButtonTertiary?.value
    const tertiaryButtonLink = hasAnchorLinkTertiary ? anchorUrlTertiary : buttonLinkTertiaryValue

    const appStoreImageValue = getValue(appStoreImage, 'appStoreImage')
    const appStoreLinkValue = getValue(appStoreLink, 'appStoreLink')
    const appStoreAltTextValue = getValue(appStoreAltText, 'appStoreAltText')
    const playStoreImageValue = getValue(playStoreImage, 'playStoreImage')
    const playStoreLinkValue = getValue(playStoreLink, 'playStoreLink')
    const playStoreAltTextValue = getValue(playStoreAltText, 'playStoreAltText')

    const displayAppStore = appStoreImageValue && appStoreLinkValue
    const displayPlayStore = playStoreImageValue && playStoreLinkValue
    const displayStoreButtonsSection = displayAppStore && displayPlayStore

    const getImageForTeaser = () => {
      return (
        <ReactImage
          imgCss='col-md-12 col-lg-5 squareImage'
          alt={altImageValue}
          src={getImageSrc(imageValue as string)}
          asBG
        ></ReactImage>
      )
    }
    return (
      <div className='react-standard-teaser-wrapper' id={contentFragmentIdValue || id}>
        <OffersLoaded />
        <div data-testid={`react-standard-teaser-container`} className={`react-standard-teaser-container`}>
          {imageAlignment === ImageAlignment.LEFT && variation === Variation.withimage && getImageForTeaser()}
          <div
            className={`react-standard-teaser-card ${cardType}-teaser-card ${backgroundVariant} ${
              variantClass[variation]
            }  col-md-12 ${variation === Variation.withimage ? 'col-lg-7' : 'col-lg-12'}`}
          >
            <div className='teaser-top'>
              {contentBadgeTextValue && (
                <ReactBadge
                  contentBadgeText={contentBadgeTextValue}
                  contentBadgeColor={contentBadgeColorValue}
                  isInContainer={true}
                />
              )}
              {eyebrowValue && <ReactText className='eyebrow' text={eyebrowValue} isParsed />}
              {headingValue && <ReactText className='heading' text={headingValue} isParsed />}
              {descriptionValue && (
                <ReactText className={`description ${cardType}-description`} text={descriptionValue} isParsed />
              )}
            </div>
            {cardType === TeaserType.featured && priceValue && (
              <ReactPrice
                variant={PriceVariant.DEFAULT}
                price={priceValue}
                scratchPrice={scratchPriceValue}
                term={termValue}
                additionalNote={discountValue}
                priceTagIcon={priceTagIcon}
                cardType={cardType}
              />
            )}
            <div className='teaser-buttons'>
              {buttonTextPrimaryValue.length > 0 && (
                <span className='button-primary'>
                  {buttonTextPrimaryValue && (
                    <ReactButton
                      buttonTypes={getButtonTypeForPrimary() as ButtonTypes}
                      text={buttonTextPrimaryValue}
                      anchor={anchorIdPrimary}
                      linkType={linkTypeButtonPrimaryValue as LinkTypes}
                      linkUrl={primaryButtonLink}
                      openInNewTab={Boolean(buttonLinkNewTabPrimaryValue)}
                      linkId={linkIdButtonPrimaryValue}
                      index={0}
                      ruleExpression={ruleExpression}
                    />
                  )}
                </span>
              )}
              {buttonTextSecondaryValue && (
                <span className='button-secondary'>
                  {buttonTextSecondaryValue && (
                    <ReactButton
                      buttonTypes={buttonLinkTypeSecondaryValue as ButtonTypes}
                      text={buttonTextSecondaryValue}
                      anchor={anchorIdSecondary}
                      linkType={linkTypeButtonSecondaryValue as LinkTypes}
                      linkUrl={secondaryButtonLink}
                      openInNewTab={Boolean(buttonLinkNewTabSecondaryValue)}
                      linkId={linkIdButtonSecondaryValue}
                      index={0}
                      ruleExpression={ruleExpression}
                    />
                  )}
                </span>
              )}
              {buttonTextTertiaryValue && (
                <span className='button-tertiary'>
                  {buttonTextTertiaryValue && (
                    <ReactButton
                      buttonTypes={buttonLinkTypeTertiaryValue as ButtonTypes}
                      text={buttonTextTertiaryValue}
                      anchor={anchorIdTertiary}
                      linkType={linkTypeButtonTertiaryValue as LinkTypes}
                      linkUrl={tertiaryButtonLink}
                      openInNewTab={Boolean(buttonLinkNewTabTertiaryValue)}
                      linkId={linkIdButtonTertiaryValue}
                      index={0}
                      ruleExpression={ruleExpression}
                    />
                  )}
                </span>
              )}
              {displayStoreButtonsSection && (
                <div className='store-buttons-section'>
                  <StoreButton
                    linkValue={appStoreLinkValue}
                    imageValue={appStoreImageValue}
                    altTextValue={appStoreAltTextValue}
                    size={'sm'}
                  />
                  <StoreButton
                    linkValue={playStoreLinkValue}
                    imageValue={playStoreImageValue}
                    altTextValue={playStoreAltTextValue}
                    size={'sm'}
                  />
                </div>
              )}
            </div>
          </div>
          {imageAlignment === ImageAlignment.RIGHT && variation === Variation.withimage && getImageForTeaser()}
        </div>
      </div>
    )
  }

  const getDynamicMediaComponentName = () => {
    if (cardType === TeaserType.commerce) {
      return DynamicMediaComponents.COMMERCE_TEASER
    } else {
      return DynamicMediaComponents.TEASER
    }
  }

  if (flexOffers?.loading) {
    return <ReactStandardTeaserSkeleton variation={variation} cardType={cardType} />
  }

  return (
    <>
      {contentFragments.length > 0 &&
        contentFragments.map((item: ContentFragment, index: number) => {
          const elements = item?.elements || {}
          const id = item?.id
          let conditionResult = true
          if (flexOffers?.success && ruleType === RuleType.conditional && condition) {
            conditionResult = getConditionResult(condition, flexOffers, index)
          }
          if (conditionResult && index === 0 && Object.keys(elements)?.length > 0) {
            return <Fragment key={index}>{renderCard(elements, id)}</Fragment>
          }
        })}
    </>
  )
}
export default ReactStandardTeaser
